import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { deleteBd, getTableBd, getTableByIdBd, insertTableBd } from '../../config/functions';

const ModalPlantillaOpt = ({
    rig,
    setRig,
    datosInferiores,
    setDatosInferiores,
    setIsLoading,
    tipo,
    show,
    handleClose,
    user,
    uid,
    variables, // Lista completa de variables para mostrar nombres
    onPlantillaAplicada, // Callback para recargar datos después de aplicar plantilla
    theme
}) => {

    const [plantillas, setPlantillas] = useState([]);
    const [datosPlantillas, setDatosPlantillas] = useState([]);
    const [datosPlantillasIzquierda, setDatosPlantillasIzquierda] = useState([]);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
    const [isLoadingP, setIsLoadingP] = useState(false);

    // Aplicar tema por defecto si no se proporciona
    const currentTheme = theme || {
        bgColor: '#000000',
        fontColor: '#ffffff',
        chartBorder: '#333333'
    };

    // Cargar plantillas disponibles al abrir modal y resetear estados
    useEffect(() => {
        // Solo consultar plantillas si el modal está visible Y hay un rig válido
        if (!show || !rig || rig === 0) {
            return;
        }

        // Resetear estados al abrir el modal
        setIsLoadingP(false);
        setPlantillaSeleccionada('');
        setDatosPlantillas([]);
        setDatosPlantillasIzquierda([]);

        const cargarPlantillas = async () => {
            try {
                const pl = await getTableBd('getPlantillas');
                if (pl && Array.isArray(pl)) {
                    setPlantillas(pl);
                } else {
                    setPlantillas([]);
                }
            } catch (error) {
                console.error('Error cargando plantillas:', error);
                toast.error('Error cargando plantillas');
                setPlantillas([]);
            }
        };
        cargarPlantillas();
    }, [show, rig]);

    // Propagar estado de loading al componente padre
    useEffect(() => {
        setIsLoading(isLoadingP);
    }, [isLoadingP, setIsLoading]);

    // Cargar datos de la plantilla seleccionada
    const getDatosPlantilla = async (plantillaIdn) => {
        try {
            const dpl = await getTableByIdBd(plantillaIdn, 'getdataplantillagraph');
            if (dpl && Array.isArray(dpl)) {
                setDatosPlantillas(dpl);
            } else {
                setDatosPlantillas([]);
            }
            // Cargar también los datos de la grilla izquierda
            await getDatosPlantillaIzq(plantillaIdn);
        } catch (error) {
            console.error('Error cargando datos de plantilla:', error);
            toast.error('Error cargando datos de plantilla');
            setDatosPlantillas([]);
            setDatosPlantillasIzquierda([]);
        }
    };

    // Cargar datos de la grilla izquierda de la plantilla
    const getDatosPlantillaIzq = async (plantillaIdn) => {
        try {
            const data = await getTableByIdBd(plantillaIdn, 'getdataplantillagraphIzq');
            if (data && Array.isArray(data) && data.length > 0) {
                setDatosPlantillasIzquierda(data);
            } else {
                setDatosPlantillasIzquierda([]);
            }
        } catch (error) {
            console.error('Error cargando datos de plantilla izquierda:', error);
            setDatosPlantillasIzquierda([]);
        }
    };

    // Manejar cambio de plantilla seleccionada
    const handlePlantillaChange = (e) => {
        const plantillaId = e.target.value;
        setPlantillaSeleccionada(plantillaId);
        if (plantillaId) {
            getDatosPlantilla(plantillaId);
        } else {
            setDatosPlantillas([]);
            setDatosPlantillasIzquierda([]);
        }
    };

    // Aplicar plantilla seleccionada
    const saveDefault = async () => {
        if (!plantillaSeleccionada) {
            toast.warning('Seleccione una plantilla');
            return;
        }

        if (!datosPlantillas || datosPlantillas.length === 0) {
            toast.warning('La plantilla seleccionada no tiene datos');
            return;
        }

        const tldroUser = rig + ':' + uid;
        const tldroUname = rig + ':' + user;

        setIsLoadingP(true);
        try {
            // 1. Eliminar datos actuales del usuario (gráficas Y grilla izquierda)
            await deleteBd(tldroUname, 'deletedatamatrizgraphusuario');
            await deleteBd(tldroUser, 'deletedatamatrizgraphizqusuario');

            // 2. Crear plantilla completa (insertar nuevas variables)
            await crearPlantillaCompleta();

            // 3. Crear plantilla de grilla izquierda
            await crearPlantillaCompletaIzq();

            // 4. Limpiar estado local
            setDatosInferiores([]);

            // 5. Notificar éxito
            toast.success('✅ Plantilla asignada correctamente. Recargando...');

            // 6. Cerrar modal
            handleClose();

            // 7. Ejecutar callback para recargar datos (limpia estados + reinicializa)
            if (onPlantillaAplicada) {
                onPlantillaAplicada();
            }

        } catch (error) {
            console.error('Error aplicando plantilla:', error);
            toast.error('Error al aplicar la plantilla');
        } finally {
            setIsLoadingP(false);
        }

    };

    // Crear plantilla completa insertando todas las variables SECUENCIALMENTE
    const crearPlantillaCompleta = async () => {
        try {
            const fecha = new Date();

            // Insertar una por una de forma SECUENCIAL (no paralelo) para evitar saturar el servidor
            for (const dp of datosPlantillas) {
                const dataSend = {
                    nb_tldro_idn: parseInt(rig),
                    nb_var_idn: parseInt(dp.nb_var_idn),
                    vr_adt_usureg: user,
                    dt_adt_fchreg: fecha.toISOString(),
                    nb_tldvrb_ord: dp.nb_grafica_orden,
                    nb_tldvrb_min: dp.nb_grafica_minimo,
                    nb_tldvrb_max: dp.nb_grafica_maximo
                };

                // Usar el endpoint correcto: createvarmatrizgraph (no insertarvarmatrizgraph)
                await insertTableBd('createvarmatrizgraph', dataSend);
            }

        } catch (error) {
            console.error('Error creando plantilla completa:', error);
            throw error;
        }
    };

    // Crear plantilla de grilla izquierda insertando variables SECUENCIALMENTE
    const crearPlantillaCompletaIzq = async () => {
        try {
            if (!datosPlantillasIzquierda || datosPlantillasIzquierda.length === 0) return;

            const fecha = new Date();

            // Insertar una por una de forma SECUENCIAL
            for (const dp of datosPlantillasIzquierda) {
                const dataSendIzq = {
                    nb_tldro_idn: parseInt(rig),
                    nb_var_idn: parseInt(dp.nb_var_idn),
                    nb_usurio_idn: parseInt(uid),
                    nb_datos_grafica_orden: parseInt(dp.nb_datos_orden),
                    vr_adt_usureg: user,
                    dt_adt_fchreg: fecha.toISOString()
                };
                await insertTableBd('createvarmatrizizq', dataSendIzq);
            }

        } catch (error) {
            console.error('Error creando plantilla de grilla izquierda:', error);
            // No lanzar error para no bloquear el proceso principal
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" dialogClassName="modal-themed-dialog">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa-regular fa-object-group me-2"></i>
                    Asignación de Plantillas
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {isLoadingP ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        <p className="mt-3 text-muted">Aplicando plantilla, por favor espere...</p>
                    </div>
                ) : (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Seleccione una plantilla:</Form.Label>
                            <Form.Select
                                value={plantillaSeleccionada}
                                onChange={handlePlantillaChange}
                                className="bg-secondary text-white"
                            >
                                <option value="">-- Seleccione --</option>
                                {plantillas && plantillas.length > 0 ? (
                                    plantillas.map((pl) => (
                                        <option key={pl.nb_plantilla_idn} value={pl.nb_plantilla_idn}>
                                            {pl.vr_plantilla_nombre}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>No hay plantillas disponibles</option>
                                )}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                {plantillas.length > 0
                                    ? `${plantillas.length} plantilla(s) disponible(s)`
                                    : 'Cargando plantillas...'}
                            </Form.Text>
                        </Form.Group>

                        {datosPlantillas.length > 0 && (
                            <div className="mt-3">
                                <h6>Vista previa de la plantilla (Gráficas):</h6>
                                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-dark table-striped">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Track</th>
                                                <th>Variable</th>
                                                <th>Mínimo</th>
                                                <th>Máximo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {datosPlantillas
                                                .sort((a, b) => a.nb_grafica_orden - b.nb_grafica_orden)
                                                .map((dp, idx) => {
                                                    const orden = parseInt(dp.nb_grafica_orden) /* + 1 */;
                                                    const trackNum = Math.floor(orden / 2) + 1;
                                                    const variable = variables?.find(v => parseInt(v.nb_var_idn) === parseInt(dp.nb_var_idn));
                                                    const varNombre = variable?.vr_var_nom || dp.vr_var_nom || `ID: ${dp.nb_var_idn}`;


                                                    if (orden <= 9) {
                                                        return (
                                                            <tr key={idx}>
                                                                <td className='text-center'>{orden}</td>
                                                                <td className='text-center'>
                                                                    <span className="badge" style={{
                                                                        backgroundColor: `hsl(${(trackNum - 1) * 60}, 70%, 50%)`,
                                                                        color: 'white'
                                                                    }}>
                                                                        Track {trackNum}
                                                                    </span>
                                                                </td>
                                                                <td>{varNombre}</td>
                                                                <td>{dp.nb_grafica_minimo}</td>
                                                                <td>{dp.nb_grafica_maximo}</td>
                                                            </tr>
                                                        );
                                                    }
                                                })
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {datosPlantillasIzquierda.length > 0 && (
                            <div className="mt-3">
                                <h6>Vista previa de la plantilla (Grilla Izquierda):</h6>
                                <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-dark table-striped">
                                        <thead>
                                            <tr>
                                                <th>Posición</th>
                                                <th>Variable</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {datosPlantillasIzquierda
                                                .sort((a, b) => a.nb_datos_orden - b.nb_datos_orden)
                                                .map((dp, idx) => {
                                                    const variable = variables?.find(v => parseInt(v.nb_var_idn) === parseInt(dp.nb_var_idn));
                                                    const varNombre = variable?.vr_var_nom || dp.vr_var_nom || `ID: ${dp.nb_var_idn}`;
                                                    return (
                                                        <tr key={idx}>
                                                            <td className='text-center'>{parseInt(dp.nb_datos_orden)}</td>
                                                            <td>{varNombre}</td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="alert alert-warning mt-3" role="alert">
                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                            <strong>Advertencia:</strong> Al aplicar una plantilla, se eliminarán todas las variables
                            configuradas actualmente para este rig (tanto en gráficas como en grilla izquierda) y se reemplazarán con las de la plantilla seleccionada.
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoadingP}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={saveDefault}
                    disabled={isLoadingP || !plantillaSeleccionada || datosPlantillas.length === 0}
                >
                    {isLoadingP ? (
                        <>
                            <i className="fa-solid fa-spinner fa-spin me-2"></i>
                            Aplicando...
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-check me-2"></i>
                            Aplicar Plantilla
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalPlantillaOpt;
